import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UUIDSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import {
  DBJsonObjectSchema,
  DBNumericSchema,
  DBSha256Schema,
  DBTimestampSchema,
  JsonObjectSchema,
  PageFractionSchema,
  Sha256Schema,
} from '../common/sql';
import { QuestionDifficultySchema } from '../questionEnrichments/schemas';
import { QUESTION_DIFFICULTY_MAX, QUESTION_DIFFICULTY_MIN } from './constants';
import { QuestionStatus } from './enums';

const LATEX_MAX = 20000;
const LatexSchema = z.string().min(1).max(LATEX_MAX);
/**
 * A node's own text. It can be empty on a node whose parts hold all the text,
 * such as a question number printed above its parts. An empty leaf is a failed
 * transcription, which the digitiser rejects.
 */
const NodeLatexSchema = z.string().max(LATEX_MAX);
const PlaceholderTokenSchema = z
  .string()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Must be a lowercase hyphenated slug');

/**
 * DB Schemas
 *
 * A root question and each of its parts are all rows in `questions`. `status`
 * is nullable because a CHECK allows it on roots only.
 */

export const DBQuestionStatusSchema = z.enum(QuestionStatus);

export const DBQuestionSchema = z.object({
  id: UUIDSchema,
  parentQuestionId: UUIDSchema.nullable(),
  position: z.number().int().min(0),
  partLabel: z.string().min(1).max(32).nullable(),
  bodyLatex: NodeLatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  status: DBQuestionStatusSchema.nullable(),
  subjectId: UUIDSchema.nullable(),
  independentlyUsable: z.boolean(),
  marks: z.number().int().min(0).nullable(),
  optionHeadings: z.array(LatexSchema),
  derivedFromQuestionId: UUIDSchema.nullable(),
  supersededByQuestionId: UUIDSchema.nullable(),
  selfHash: DBSha256Schema.nullable(),
  treeHash: DBSha256Schema.nullable(),
  createdBy: CognitoUserIdSchema.nullable(),
  publishedBy: CognitoUserIdSchema.nullable(),
  publishedAt: DBTimestampSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBStimulusSchema = z.object({
  id: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBQuestionCriterionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  descriptorLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  marks: z.number().int().min(0),
  provenanceId: UUIDSchema,
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBCriterionPointSchema = z.object({
  id: UUIDSchema,
  criterionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBExemplarAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  provenanceId: UUIDSchema,
  sourceSnippetAssetId: UUIDSchema.nullable(),
  isPrimary: z.boolean(),
  position: z.number().int().min(0),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBAlternativeAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  provenanceId: UUIDSchema,
  notes: AllCharLabelSchema.min(1).max(2048).nullable(),
  position: z.number().int().min(0),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBMarkerNoteSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  provenanceId: UUIDSchema,
  position: z.number().int().min(0),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBQuestionOptionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema,
  cells: z.array(LatexSchema),
  latexValid: z.boolean(),
  latexValidation: DBJsonObjectSchema,
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBCorrectOptionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  optionId: UUIDSchema,
  provenanceId: UUIDSchema,
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * The option columns shown to students. Listed one by one so a new option
 * column stays hidden from students until it is added here.
 */
const displayOptionColumns = {
  id: true,
  questionId: true,
  position: true,
  bodyLatex: true,
  cells: true,
  latexValid: true,
  latexValidation: true,
  createdAt: true,
  updatedAt: true,
} as const satisfies Partial<
  Record<keyof typeof DBQuestionOptionSchema.shape & keyof typeof QuestionOptionSchema.shape, true>
>;

/** An option as a student sees it: only the columns `displayOptionColumns` lists. */
export const DBQuestionDisplayOptionSchema = DBQuestionOptionSchema.pick(displayOptionColumns);

/** The longest caption a diagram stores. */
export const QUESTION_DIAGRAM_CAPTION_MAX_LENGTH = 512;
/** The longest description a diagram stores. */
export const QUESTION_DIAGRAM_DESCRIPTION_MAX_LENGTH = 2000;

export const DBQuestionDiagramSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema.nullable(),
  stimulusId: UUIDSchema.nullable(),
  assetId: UUIDSchema,
  placeholderToken: PlaceholderTokenSchema,
  caption: AllCharLabelSchema.min(1).max(QUESTION_DIAGRAM_CAPTION_MAX_LENGTH).nullable(),
  description: AllCharLabelSchema.min(1).max(QUESTION_DIAGRAM_DESCRIPTION_MAX_LENGTH).nullable(),
  sourcePage: z.number().int().positive().nullable(),
  bboxX0: DBNumericSchema.nullable(),
  bboxY0: DBNumericSchema.nullable(),
  bboxX1: DBNumericSchema.nullable(),
  bboxY1: DBNumericSchema.nullable(),
  position: z.number().int().min(0),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

/**
 * The diagram columns shown to students. Listed one by one so a new diagram
 * column stays hidden from students until it is added here.
 */
const displayDiagramColumns = {
  id: true,
  questionId: true,
  stimulusId: true,
  assetId: true,
  placeholderToken: true,
  caption: true,
  sourcePage: true,
  bboxX0: true,
  bboxY0: true,
  bboxX1: true,
  bboxY1: true,
  position: true,
  createdAt: true,
  updatedAt: true,
} as const satisfies Partial<
  Record<
    keyof typeof DBQuestionDiagramSchema.shape & keyof typeof QuestionDiagramSchema.shape,
    true
  >
>;

/**
 * A diagram as a student sees it, without `description`, which can state what a
 * question asks the student to read off the figure.
 */
export const DBQuestionDisplayDiagramSchema = DBQuestionDiagramSchema.pick(displayDiagramColumns);

export const DBQuestionStimulusLinkSchema = z.object({
  questionId: UUIDSchema,
  stimulusId: UUIDSchema,
  position: z.number().int().min(0),
  createdAt: DBTimestampSchema,
});

export const DBQuestionDependencySchema = z.object({
  id: UUIDSchema,
  fromQuestionId: UUIDSchema,
  toQuestionId: UUIDSchema,
  dependencyTypeId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

/**
 * One leaf in the bank-wide question list. Status is the root's; marks are the
 * leaf's own, null while they are not known; difficulty is null while the leaf
 * has no score on a difficulty scale.
 */
export const DBQuestionLeafSummarySchema = z.object({
  id: UUIDSchema,
  rootQuestionId: UUIDSchema,
  bodyLatex: NodeLatexSchema,
  partPath: z.string().min(1).nullable(),
  status: DBQuestionStatusSchema,
  types: z.array(z.object({ id: UUIDSchema, code: z.string(), label: z.string() })),
  marks: z.number().int().min(0).nullable(),
  difficulty: z.number().min(QUESTION_DIFFICULTY_MIN).max(QUESTION_DIFFICULTY_MAX).nullable(),
  attempts: z.number().int().min(0),
  sourceLabel: z.string().nullable(),
  sourceStyle: z.string().nullable(),
  year: z.number().int().nullable(),
  originalQuestionId: z.string().nullable(),
  originalQuestionNumber: z.string().nullable(),
});

/** A root's status, with the types on any node of its tree. */
export const DBQuestionRootSummarySchema = z.object({
  questionId: UUIDSchema,
  status: DBQuestionStatusSchema,
  types: z.array(z.object({ id: UUIDSchema, code: z.string(), label: z.string() })),
});

/** The printing a question is listed under: its root's latest occurrence. */
export const DBQuestionSourceSchema = z.object({
  sourceDocumentId: UUIDSchema,
  sourceLabel: z.string(),
  sourceStyle: z.string(),
  year: z.number().int().nullable(),
  originalQuestionId: z.string().min(1),
  originalQuestionNumber: z.string().min(1),
});

/**
 * Client Schemas
 */

export const QuestionStatusSchema = z.enum(QuestionStatus).openapi({
  description: 'Lifecycle status, set on roots only. Parts follow their root.',
  example: QuestionStatus.Published,
});
export const QuestionIdSchema = UUIDSchema;
export const QuestionBodyLatexSchema = NodeLatexSchema.openapi({
  description: 'This node’s own text, in LaTeX. Empty on a node whose parts hold all the text.',
  example: 'A projectile is launched at 25 m/s.',
});
export const QuestionSelfHashSchema = Sha256Schema;
export const QuestionPartLabelSchema = z.string().min(1).max(32).nullable().openapi({
  description:
    'The label the source document gave this part, relative to its parent: 7(b)(ii) is stored as (ii) under part (b). Kept for provenance only.',
  example: '(b)',
});
export const QuestionMarksSchema = z
  .number()
  .int()
  .min(0)
  .nullable()
  .openapi({
    description:
      'The marks a leaf is worth, as printed. Null on a node with parts, whose marks are the ' +
      'sum of its leaves, and on a leaf whose marks are not yet known.',
    example: 3,
  });
export const QuestionOptionHeadingsSchema = z.array(LatexSchema).openapi({
  description:
    'The column headings printed once above choices laid out as a table, in order. Empty ' +
    'unless the choices are printed as a table, and then only on a leaf with options.',
  example: ['Charge', 'Mass'],
});
export const QuestionOptionCellsSchema = z.array(LatexSchema).openapi({
  description:
    'This option’s entry under each of its question’s option headings, in the same order. ' +
    'Empty unless its question has option headings; bodyLatex names each entry by its heading.',
  example: ['$q$', '$2m$'],
});

export const QuestionSchema = z.object({
  id: UUIDSchema,
  parentQuestionId: UUIDSchema.nullable(),
  position: z.number().int().min(0),
  partLabel: QuestionPartLabelSchema,
  bodyLatex: QuestionBodyLatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  status: QuestionStatusSchema.nullable(),
  subjectId: UUIDSchema.nullable(),
  independentlyUsable: z.boolean(),
  marks: QuestionMarksSchema,
  optionHeadings: QuestionOptionHeadingsSchema,
  derivedFromQuestionId: UUIDSchema.nullable(),
  supersededByQuestionId: UUIDSchema.nullable(),
  selfHash: Sha256Schema.nullable(),
  treeHash: Sha256Schema.nullable(),
  createdBy: CognitoUserIdSchema.nullable(),
  publishedBy: CognitoUserIdSchema.nullable(),
  publishedAt: UnixTimestampSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const StimulusSchema = z.object({
  id: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const QuestionCriterionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  descriptorLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  marks: z
    .number()
    .int()
    .min(0)
    .openapi({
      description:
        'Whole marks, added across criteria. Consecutive half-mark criteria in the source are ' +
        'joined into one criterion worth their total.',
      example: 2,
    }),
  provenanceId: UUIDSchema,
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const CriterionPointSchema = z.object({
  id: UUIDSchema,
  criterionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema.openapi({
    description: 'One of the things this criterion requires before its marks are awarded.',
    example: 'States that the reaction is exothermic',
  }),
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const ExemplarAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  provenanceId: UUIDSchema,
  sourceSnippetAssetId: UUIDSchema.nullable(),
  isPrimary: z.boolean(),
  position: z.number().int().min(0),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const AlternativeAnswerSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  provenanceId: UUIDSchema,
  notes: AllCharLabelSchema.min(1).max(2048).nullable(),
  position: z.number().int().min(0),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const MarkerNoteSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  bodyLatex: LatexSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  provenanceId: UUIDSchema,
  position: z.number().int().min(0),
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const QuestionOptionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  bodyLatex: LatexSchema,
  cells: QuestionOptionCellsSchema,
  latexValid: z.boolean(),
  latexValidation: JsonObjectSchema,
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const CorrectOptionSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema,
  optionId: UUIDSchema.openapi({ description: 'The option the question accepts' }),
  provenanceId: UUIDSchema,
  createdBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

/** An option as a student sees it: only the columns `displayOptionColumns` lists. */
export const QuestionDisplayOptionSchema = QuestionOptionSchema.pick(displayOptionColumns);

export const QuestionDiagramSchema = z.object({
  id: UUIDSchema,
  questionId: UUIDSchema.nullable(),
  stimulusId: UUIDSchema.nullable(),
  assetId: UUIDSchema,
  placeholderToken: PlaceholderTokenSchema.openapi({
    description:
      'The slug in the [[diagram:…]] token placing the figure: in its owner’s body, or in an ' +
      'option of the question that owns it',
    example: 'projectile-path',
  }),
  caption: AllCharLabelSchema.min(1).max(QUESTION_DIAGRAM_CAPTION_MAX_LENGTH).nullable().openapi({
    description: 'The label or title printed with the figure',
    example: 'Figure 1',
  }),
  description: AllCharLabelSchema.min(1)
    .max(QUESTION_DIAGRAM_DESCRIPTION_MAX_LENGTH)
    .nullable()
    .openapi({
      description: 'What the figure shows, in plain words. Never printed on the paper',
      example: 'A circuit with a 6 V battery and two 3 Ω resistors in series',
    }),
  sourcePage: z.number().int().positive().nullable(),
  bboxX0: PageFractionSchema.nullable(),
  bboxY0: PageFractionSchema.nullable(),
  bboxX1: PageFractionSchema.nullable(),
  bboxY1: PageFractionSchema.nullable(),
  position: z.number().int().min(0),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

/** A diagram as a student sees it, without `description`. */
export const QuestionDisplayDiagramSchema = QuestionDiagramSchema.pick(displayDiagramColumns);

/** Where a stimulus sits among a node's parts. Stimuli and parts share one position sequence. */
export const QuestionStimulusLinkSchema = z.object({
  questionId: UUIDSchema,
  stimulusId: UUIDSchema,
  position: z.number().int().min(0),
  createdAt: UnixTimestampSchema,
});

/** A dependency between two parts of the same question. */
export const QuestionDependencySchema = z.object({
  id: UUIDSchema,
  fromQuestionId: UUIDSchema,
  toQuestionId: UUIDSchema,
  dependencyTypeId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});

export const QuestionLeafSummarySchema = z.object({
  id: UUIDSchema,
  rootQuestionId: UUIDSchema,
  bodyLatex: QuestionBodyLatexSchema,
  partPath: z.string().min(1).nullable().openapi({
    description: "The leaf's part labels below its root, or null for a question with no parts",
    example: '(b)(ii)',
  }),
  status: QuestionStatusSchema,
  types: z.array(z.object({ id: UUIDSchema, code: z.string(), label: z.string() })),
  marks: z.number().int().min(0).nullable().openapi({
    description: "The leaf's own marks, as printed. Null while they are not known.",
  }),
  difficulty: QuestionDifficultySchema.shape.difficulty.nullable(),
  attempts: z.number().int().min(0).openapi({
    description: 'Responses to the leaf, leaving out voided attempts',
  }),
  sourceLabel: z.string().nullable(),
  sourceStyle: z.string().nullable(),
  year: z.number().int().nullable(),
  originalQuestionId: z.string().nullable().openapi({
    description: 'What the listed source prints beside the question the leaf belongs to',
    example: '7(b)',
  }),
  originalQuestionNumber: z.string().nullable().openapi({
    description: 'The number the listed source prints the question under',
    example: '7',
  }),
});

export const QuestionRootSummarySchema = z.object({
  questionId: UUIDSchema.openapi({ description: 'The root question' }),
  status: QuestionStatusSchema,
  types: z
    .array(z.object({ id: UUIDSchema, code: z.string(), label: z.string() }))
    .openapi({ description: 'Each type on the root or any of its parts, once' }),
});

export const QuestionSourceSchema = z.object({
  sourceDocumentId: UUIDSchema,
  sourceLabel: z.string().openapi({
    description: 'The document’s display name, else its paper title, else its file name',
    example: '2023 Physics Trial HSC Examination',
  }),
  sourceStyle: z.string(),
  year: z.number().int().nullable(),
  originalQuestionId: z.string().min(1).openapi({
    description: 'What the document prints beside the question',
    example: '7(b)',
  }),
  originalQuestionNumber: z.string().min(1).openapi({
    description: 'The number the document prints the question under',
    example: '7',
  }),
});
